import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { useSchedule, useScheduleFreshness } from "../../../entities/schedule";
import {
  DispositionBoardWidget,
  DispositionToolbar,
  DispositionWeekWidget,
  useDispositionDay,
  useDispositionView,
  useDispositionWeek,
} from "../../../widgets/disposition-board";

export const DispositionPage = () => {
  // Owned here so the navigation can sit in the page header while the widget renders the grid.
  // Both day and week read the same `?day=` anchor, so mounting the two hooks side by side is
  // cheap — only the widget that is on screen fetches anything.
  const view = useDispositionView();
  const day = useDispositionDay();
  const week = useDispositionWeek();

  const isWeek = view.value === "woche";
  const range = isWeek ? week : day;

  /**
   * The freshness of the board on screen, read without moving anyone's fetch.
   *
   * `useDispositionBoard` and `useDispositionWeekBoard` both call
   * `useSchedule({ from, to, includeWorkOrder: true })` internally but narrow their return to
   * `{ rows, isLoading, isError, refetch }`, so the timestamps never reach this layer. Rather than
   * widening both hooks or lifting the fetch out of the widgets, this subscribes to the **same
   * query key** — one extra observer on a cache entry that already exists, and no extra request.
   *
   * That only holds while the arguments match exactly. If a widget ever changes its range or drops
   * `includeWorkOrder`, this becomes a second cache entry and a second request rather than a
   * compile error, so the three call sites have to move together.
   */
  const scheduleQuery = useSchedule({
    from: range.from,
    to: range.to,
    includeWorkOrder: true,
  });
  const { lastUpdatedAt, isFetching, isDisconnected } = useScheduleFreshness([scheduleQuery]);

  return (
    <PageLayout
      pageTitle="Kalender"
      scrollable={false}
      headerChildren={
        <DispositionToolbar
          view={view}
          day={day}
          week={week}
          lastUpdatedAt={lastUpdatedAt}
          isFetching={isFetching}
          isDisconnected={isDisconnected}
          onRefresh={() => {
            void scheduleQuery.refetch();
          }}
        />
      }
    >
      {isWeek ? (
        <DispositionWeekWidget week={week} />
      ) : (
        <DispositionBoardWidget day={day} />
      )}
    </PageLayout>
  );
};
